import React, {useState, useEffect, useContext} from "react";
import axios from "axios";
import withAuth from "../../auth";
import Pagination from "../Pagination";
import moment from 'moment';
import { API_URL } from '../../constants';
import {Link, useNavigate} from "react-router-dom";
import {AuthContext} from "../AuthContext";
import echo from "../../echo";
import WhitelabelBadge from "../WhitelabelBadge";
import {
    formatPickupDropoffDisplay,
    formatPickupOriginDisplay,
} from '../../utils/locationDisplay';

const PickupLiveRequests = () => {
    const [pickups, setPickups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        // id: '',
        // user_id,
        // truck_type_id,
        // driver_id,
        // created_at_start_date: '',
        // created_at_end_date: '',
    });
    const navigate = useNavigate();
    const {token, admin, setToken, hasPermission} = useContext(AuthContext);

    useEffect(() => {
        if (!admin?.id) return;

        const channel = echo.channel('np-admin-pickups');

        channel.listen('.App\\Events\\Admin\\PickupUpdated', (event) => {
            if (event.message_type === 'pickup_updated' && event.pickup) {
                if (event.pickup.delivery_status?.id !== 5 && event.pickup.delivery_status?.id !== 6 && event.pickup.waiting_for_driver_at) {
                    console.log('event.pickup', event.pickup);
                    setPickups((prev) => [event.pickup, ...prev]);
                }
            }
        });

        return () => {
            echo.leave('np-admin-pickups');
        };
    }, [admin]);

    useEffect(() => {
        if (!pickups.length) return;

        const channels = pickups.map((p) => p.channel_id).filter(Boolean);

        channels.forEach((channelId) => {
            const c = echo.channel(channelId);

            c.listen('.App\\Events\\Admin\\PickupUpdated', (event) => {
                const updatedPickup = event.pickup;
                if (!updatedPickup) return;

                setPickups((prevPickups) =>
                    prevPickups.map((p) =>
                        p.id === updatedPickup.id
                            ? { ...p, ...updatedPickup }
                            : p
                    )
                );
            });
        });

        return () => {
            channels.forEach((channelId) => {
                echo.leave(channelId);
            });
        };
    }, [pickups]);

    useEffect(() => {
        fetchPickups();
    }, [currentPage]);

    const fetchPickups = async (newFilters = null) => {
        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/admin/pickups/live-requests`,
                {
                    page: currentPage,
                    ...(newFilters || filters),
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setPickups(response.data.data);
            setTotalPages(response.data.last_page);
        } catch (error) {
            console.error("There was an error fetching the drivers!", error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFilterChange = (e, column) => {
        const newFilters = {
            ...filters,
            [column]: e.target.value,
        };

        const nonEmptyFilters = Object.keys(newFilters).reduce((acc, key) => {
            if (newFilters[key]) {
                acc[key] = newFilters[key];
            }
            return acc;
        }, {});

        setFilters(nonEmptyFilters);
    };

    const handleSearchSubmit = () => {
        setCurrentPage(1); // reset pagination when filter
        fetchPickups();
    };

    const handleDropdownChange = (e, column) => {
        const newFilters = {
            ...filters,
            [column]: e.target.value,
        };

        const nonEmptyFilters = Object.keys(newFilters).reduce((acc, key) => {
            if (newFilters[key]) {
                acc[key] = newFilters[key];
            }
            return acc;
        }, {});

        setFilters(nonEmptyFilters);
        setCurrentPage(1); // reset pagination when filter
        fetchPickups(nonEmptyFilters);
    };

    const statusColors = {
        '0': 'transparent',
        '1': '#FFFAE5',  // Very Light Gold
        '2': '#FFCBB2',  // Very Light OrangeRed
        '3': '#CFF3C8',  // Very Light Green
        '4': '#C7D6E9',  // Very Light SteelBlue
        '5': '#D1F2CF',  // Very Light LimeGreen
        '6': '#FFA3B5'   // Very Light Crimson
    };

    const truncateWithEllipsis = (text, maxLength = 30) => {
        if (text.length > maxLength) {
            return (
                <span title={text}>
                    {text.substring(0, maxLength)}...
                </span>
            );
        }
        return text;
    };

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Live Requests</h2>
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    {!!pickups?.length &&
                        <div className="table-responsive">
                            <table className="table table-sm table-striped text-center text-nowrap">
                                <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Whitelabel</th>
                                    <th>Status</th>
                                    <th>Customer</th>
                                    <th>Driver</th>
                                    <th>Truck Type</th>
                                    <th>Cost / Dist.</th>
                                    <th>Locations</th>
                                    <th>Booked At</th>
                                </tr>
                                <tr>
                                    {/* ID */}
                                    <td>
                                        <div className="input-group d-flex justify-content-center">
                                            <input
                                                className="form-control form-control-sm text-center"
                                                type="text"
                                                inputMode="search"
                                                value={filters.id}
                                                placeholder='ID'
                                                onChange={(e) => handleFilterChange(e, 'id')}
                                                onKeyPress={e => {
                                                    if (e.key === 'Enter') handleSearchSubmit(e)
                                                }}
                                                style={{maxWidth: '80px'}}
                                            />
                                            <button className="btn btn-sm btn-outline-secondary" onClick={handleSearchSubmit}>
                                                <i className="fa fa-search"/>
                                            </button>
                                        </div>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    {/* Customer */}
                                    <td>
                                        <div className="input-group">
                                            <input
                                                className="form-control form-control-sm text-center"
                                                type="text"
                                                inputMode="search"
                                                value={filters.user_id}
                                                placeholder='Customer ID'
                                                onChange={(e) => handleFilterChange(e, 'user_id')}
                                                onKeyPress={e => {
                                                    if (e.key === 'Enter') handleSearchSubmit(e)
                                                }}
                                            />
                                            <button className="btn btn-sm btn-outline-secondary" onClick={handleSearchSubmit}>
                                                <i className="fa fa-search"/>
                                            </button>
                                        </div>
                                    </td>
                                    {/* Driver */}
                                    <td>
                                        <div className="input-group">
                                            <input
                                                className="form-control form-control-sm text-center"
                                                type="text"
                                                inputMode="search"
                                                value={filters.driver_id}
                                                placeholder='Driver ID'
                                                onChange={(e) => handleFilterChange(e, 'driver_id')}
                                                onKeyPress={e => {
                                                    if (e.key === 'Enter') handleSearchSubmit(e)
                                                }}
                                            />
                                            <button className="btn btn-sm btn-outline-secondary" onClick={handleSearchSubmit}>
                                                <i className="fa fa-search"/>
                                            </button>
                                        </div>
                                    </td>
                                    {/* Truck Type */}
                                    <td>
                                        <div className="input-group">
                                            <select
                                                className="form-control form-control-sm text-center"
                                                value={filters.truck_type_id}
                                                onChange={(e) => handleDropdownChange(e, 'truck_type_id')}
                                            >
                                                <option value="">All</option>
                                                <option value="1">Delivery</option>
                                                <option value="2">Flatbed</option>
                                                <option value="3">Cube Van</option>
                                                <option value="4">Cargo Van</option>
                                                <option value="11">Moffett</option>
                                            </select>
                                        </div>
                                    </td>
                                    {/* Cost / Distance */}
                                    <td></td>
                                    {/* Locations */}
                                    <td></td>
                                    {/* Booked At */}
                                    <td></td>
                                </tr>
                                </thead>
                                <tbody className="text-wrap">
                                {pickups.map((pickup) => (
                                    <tr key={pickup.id}>
                                        <td style={{backgroundColor: !pickup.delivery_status?.id ? 'initial' : statusColors[pickup.delivery_status?.id] || 'initial'}}>
                                            <Link to={`/pickups/${pickup.id}`}>
                                                {pickup.id}
                                            </Link>
                                            {pickup.user && hasPermission('user-update') && (
                                                <>
                                                    <br/>
                                                    <Link to={`/pickups/create/${pickup.user.id}?duplicate=${pickup.id}`}
                                                          className='btn btn-sm btn-outline-primary'>
                                                        Duplicate
                                                    </Link>
                                                </>
                                            )}
                                        </td>
                                        <td><WhitelabelBadge whitelabelId={pickup.whitelabel_id}/></td>
                                        <td>
                                            {pickup.waiting_for_driver_at &&
                                                <div className="badge badge-light">{pickup.delivery_status?.status}</div>}
                                        </td>
                                        <td>
                                            {pickup.user &&
                                                <Link to={`/users/${pickup.user.id}`}>
                                                    {pickup.user.id}<br/>{pickup.user.full_name}
                                                </Link>
                                            }
                                        </td>
                                        <td>
                                            {pickup.driver &&
                                                <Link to={`/drivers/${pickup.driver.id}`}>
                                                    {pickup.driver.id}<br/>{pickup.driver.name}
                                                </Link>
                                            }
                                        </td>
                                        <td>{pickup.truck_type?.title}</td>
                                        <td>${pickup.cost}<br/>{pickup.distance} km</td>
                                        <td className="text-nowrap">
                                            {truncateWithEllipsis(formatPickupOriginDisplay(pickup))}<br/>
                                            {truncateWithEllipsis(formatPickupDropoffDisplay(pickup))}
                                        </td>
                                        <td>
                                            {moment(pickup.waiting_for_driver_at).format('YYYY-MM-DD HH:mm')}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            {totalPages > 1 && currentPage <= totalPages &&
                                <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange}/>
                            }
                        </div>
                    }

                    {!pickups?.length &&
                        <div className="my-4 mb-0" align="center">
                            <hr/>
                            <i>No live requests at the moment</i>
                        </div>
                    }
                </>
            )}
        </div>
    );
};

export default withAuth(PickupLiveRequests);
